import { useRef, useLayoutEffect, useEffect, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateToSearchPage } from '@/features/search/utils/searchNavigation';
import { AppLink } from '@/shared/components/AppLink';
import { Logo } from '@/shared/icons/logo';
import HeaderDesktop from '@/shared/features/header/presentation/components/HeaderDesktop';
import DesktopNavbar from '@/shared/features/menu/presentation/components/DesktopNavbar';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { useAppSelector } from '@/app/store/store';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { useRevealOnScrollUpWhile } from '@/core/hooks/useRevealOnScrollUpWhile';
import {selectDesktopRecommendedBrowseFilterStripStuck,selectMobileRecommendedSectionReached} from '@/app/store/newHomeUiSlice';
import { cn } from '@/shared/utils/cn';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { trackSearchedForProduct } from '@/features/search/constants';
import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';
import SearchModal from '@/features/search/presentation/components/SearchModal';
import { Search as SearchIcon } from 'lucide-react';
import { getActiveHomeSectionName } from '../../utils/activeHomeSection';
import { WishlistFeedbackIcon } from '@/shared/widgets/WishlistFeedbackIcon';

type HeaderLoginMode = 'login' | 'signup';

interface NewHomeHeaderProps {
  onOpenLogin?: (mode?: HeaderLoginMode) => void;
}

export default function NewHomeHeader({ onOpenLogin }: NewHomeHeaderProps) {
  const { userContext, isLoading: isUserContextLoading } = useUserContext();
  const { analytics, isInitialized } = useAnalytics();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { homePageVariant } = useMultivariateExperiment();
  const isHomePageV8 = homePageVariant === 'V8';
  const showStickySearchHeader = homePageVariant === 'V7' || isHomePageV8;
  const [isHeaderSearchModalOpen, setIsHeaderSearchModalOpen] = useState(false);
  const mobileRecommendedSectionReached = useAppSelector(selectMobileRecommendedSectionReached);
  const desktopFilterStripStuck = useAppSelector(selectDesktopRecommendedBrowseFilterStripStuck);

  /**
   * Desktop: when the user scrolls up and has scrolled past the header area, reveal the
   * full header as a fixed bar at the top of the viewport.  The navbar stays in its
   * original structural position (sibling of the header in document flow) so its own
   * sentinel-based `sticky` behaviour is not constrained by a wrapper container.
   * minScrollY ensures the fixed copy never overlaps the in-flow copy near the top.
   */
  const revealStickyHeader = useRevealOnScrollUpWhile(isDesktop, { minScrollY: 150 });

  const desktopHeaderRef = useRef<HTMLElement>(null);

  /**
   * Set --nh-sticky-header-h so that:
   *  • the sticky navbar can offset its top below the fixed header (scroll-up mode)
   *  • the recommended-section filter strip can sit flush below the header
   */
  useLayoutEffect(() => {
    const el = desktopHeaderRef.current;
    const h = revealStickyHeader && el ? el.offsetHeight : 0;
    document.documentElement.style.setProperty('--nh-sticky-header-h', `${h}px`);
  }, [revealStickyHeader]);

  /**
   * Drive the nav-logo-badge back to its "initial at-top" position while the fixed header
   * is revealed above the navbar.  The badge reacts to this attribute in CSS and uses its
   * own existing transition (transform / width / padding / left) for smooth animation.
   */
  useEffect(() => {
    const html = document.documentElement;
    if (revealStickyHeader) {
      html.dataset.nhHeaderRevealed = 'true';
    } else {
      delete html.dataset.nhHeaderRevealed;
    }
    return () => {
      delete html.dataset.nhHeaderRevealed;
    };
  }, [revealStickyHeader]);

  /** Clean up CSS variable on unmount. */
  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty('--nh-sticky-header-h');
    };
  }, []);

  const bgColor = useAppSelector(
    (s) =>
      (s as Record<string, unknown> & { headerConfig?: { mobile: { bgColor: string; logoColor: string } } })
        .headerConfig?.mobile.bgColor ?? '#ffffff'
  );
  const logoColor = useAppSelector(
    (s) =>
      (s as Record<string, unknown> & { headerConfig?: { mobile: { bgColor: string; logoColor: string } } })
        .headerConfig?.mobile.logoColor ?? '#011E39'
  );
  /** `SearchModal` often renders inside `<main z-[49]>`; this bar is `z-50` outside main, so hide it while search is open. */
  const isSearchModalOpen = useAppSelector((s) => s.searchUi.isSearchModalOpen);

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    trackSearchedForProduct(analytics, trimmed);
    navigateToSearchPage(query, (url) => navigate(url, { replace: false }));
    setIsHeaderSearchModalOpen(false);
  };

  const handleSearchIconClick = () => {
    if (analytics && isInitialized) {
      try {
        const sectionName = getActiveHomeSectionName();
        analytics.trackEvent('Search_icon_click', {
          'Page Name': 'Home',
          'Section Name': sectionName,
          'Home Page Variant': homePageVariant ?? 'NONE',
        });
      } catch (error) {
        console.error('Error tracking Search_icon_click:', error);
      }
    }
    setIsHeaderSearchModalOpen(true);
  };

  const handleWishlistIconClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (analytics && isInitialized) {
      try {
        const sectionName = getActiveHomeSectionName();
        analytics.trackEvent('Wishlist_icon_click', {
          'Page Name': 'Home',
          'Section Name': sectionName,
          'Home Page Variant': homePageVariant ?? 'NONE',
        });
      } catch (error) {
        console.error('Error tracking Wishlist_icon_click:', error);
      }
    }

    if (!userContext?.isLoggedIn && onOpenLogin) {
      event.preventDefault();
      onOpenLogin('login');
    }
  };

  return (
    <>
      {/* Mobile: logo-only bar with configured bg color */}
      <div
        className={cn(
          'hdr-mobile nh-mobile-header z-50 overflow-visible',
          showStickySearchHeader && [
            'sticky top-0 transition-transform duration-300 ease-out motion-reduce:transition-none',
            mobileRecommendedSectionReached && '-translate-y-full pointer-events-none',
          ],
          isSearchModalOpen && 'hidden',
        )}
        style={{ backgroundColor: bgColor }}
      >
        <div className="px-[20px] overflow-visible">
          <div
            className={cn(
              'flex items-center overflow-visible',
              showStickySearchHeader
                ? `justify-between ${isHomePageV8 ? 'py-2' : 'py-0.5'}`
                : 'pt-1.5 pb-1',
            )}
          >
            <div className="relative overflow-visible">
              <AppLink
                id="nh-header-logo-anchor"
                to="/"
                eventName="BlueStone Logo Clicked"
                className="relative z-10 flex items-center hover:opacity-80 transition-opacity"
                aria-label="BlueStone Logo"
              >
                <div style={{ color: logoColor }}>
                  <Logo
                    showLink={false}
                    className={cn(
                      'h-auto w-full min-w-[120px]',
                      isHomePageV8 ? 'max-w-[170px]' : 'max-w-[160px]',
                    )}
                  />
                </div>
              </AppLink>

              {/* V8 keeps the header edge straight; older variants retain the logo arc. */}
              {!isHomePageV8 && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    width: '48px',
                    height: '20px',
                    bottom: '-11px',
                    left: '-11px',
                    borderRadius: '0 0 50% 50% / 0 0 100% 100%',
                    backgroundColor: bgColor,
                    zIndex: 1,
                  }}
                  aria-hidden
                />
              )}
            </div>
            {showStickySearchHeader && (
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleSearchIconClick}
                  className={cn(
                    'flex items-center justify-center',
                    homePageVariant === 'V8'
                      ? 'text-gray-600 hover:text-gray-900 transition-colors'
                      : 'h-10 w-10',
                  )}
                  style={homePageVariant === 'V8' ? undefined : { color: logoColor }}
                  aria-label="Search"
                >
                  <SearchIcon className="h-6 w-6" aria-hidden />
                </button>

                {homePageVariant === 'V8' && (
                  <AppLink
                    to="/wishlist/view"
                    shouldRefresh
                    className="flex items-center justify-center text-gray-1000"
                    aria-label="Wishlist"
                    onClick={handleWishlistIconClick}
                  >
                    <WishlistFeedbackIcon
                      count={userContext?.wishlistCount ?? 0}
                      enabled={userContext?.isLoggedIn ?? false}
                    />
                  </AppLink>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <SearchModal
        isOpen={isHeaderSearchModalOpen}
        onClose={() => setIsHeaderSearchModalOpen(false)}
        onSearch={handleSearch}
      />

      {/*
        Desktop header: on scroll-up (revealStickyHeader) it becomes `fixed top-0` so it
        slides in above the sticky navbar.  The navbar remains a sibling in the document
        flow — its own sentinel-based sticky is unaffected and its `top` reads
        --nh-sticky-header-h so it sticks just below this header when both are visible.
      */}
      <header
        ref={desktopHeaderRef}
        className={cn(
          'hdr-desktop w-full overflow-visible shadow-sm',
          revealStickyHeader && 'fixed top-0 left-0 right-0 z-50 nh-desktop-header-reveal',
          // When the filter strip is stuck the navbar is hidden — the header is shown alone,
          // so we need `nh-desktop-header-solo` to reveal the full logo (no nav badge present).
          revealStickyHeader && desktopFilterStripStuck && 'nh-desktop-header-solo',
        )}
      >
        <HeaderDesktop
          cartCount={userContext?.cartCount ?? 0}
          wishlistCount={userContext?.wishlistCount ?? 0}
          isLoggedIn={userContext?.isLoggedIn ?? false}
          isSoftLoggedIn={userContext?.isSoftLoggedIn ?? false}
          userName={userContext?.userName}
          isUserContextLoading={isUserContextLoading}
          onSearch={handleSearch}
          onOpenLogin={onOpenLogin}
        />
      </header>
      <DesktopNavbar className="hdr-desktop" />
    </>
  );
}
